// src/utils/copyForWord.js
// Utility function to copy HTML content formatted for MS Word (portrait 8.5x11)

/**
 * Copy HTML content to clipboard in a format optimized for MS Word
 * @param {HTMLElement} contentElement - The DOM element containing the content to copy
 * @param {string} title - Optional title to include at the top
 * @returns {Promise<boolean>} - Whether the copy was successful
 */
export async function copyForWord(contentElement, title = '') {
  if (!contentElement) return false;

  try {
    // Create a styled HTML document optimized for portrait 8.5x11
    const styledHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head>
        <meta charset="utf-8">
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          @page { size: 8.5in 11in; margin: 0.75in 0.75in 0.75in 0.75in; }
          body {
            font-family: Calibri, Arial, sans-serif;
            font-size: 10pt;
            line-height: 1.35;
            color: #333;
            max-width: 7in;
          }
          h1 { font-size: 14pt; font-weight: bold; color: #1a1a1a; margin: 8pt 0 4pt 0; }
          h2 { font-size: 12pt; font-weight: bold; color: #1a1a1a; margin: 6pt 0 3pt 0; }
          h3 { font-size: 11pt; font-weight: bold; color: #1a1a1a; margin: 5pt 0 2pt 0; }
          h4 { font-size: 10pt; font-weight: bold; color: #333; margin: 4pt 0 2pt 0; }
          p { margin: 0 0 6pt 0; }
          ul, ol { margin: 0 0 6pt 0; padding-left: 18pt; }
          li { margin: 1pt 0; }
          table { border-collapse: collapse; width: 100%; margin: 6pt 0; font-size: 9pt; }
          th { background-color: #f3f4f6; border: 1px solid #d1d5db; padding: 4pt 6pt; text-align: left; font-weight: bold; }
          td { border: 1px solid #d1d5db; padding: 3pt 6pt; }
          tr:nth-child(even) { background-color: #f9fafb; }
          strong, b { font-weight: bold; }
          em, i { font-style: italic; }
          code { font-family: Consolas, monospace; font-size: 9pt; background-color: #f3f4f6; padding: 1pt 2pt; }
          pre { font-family: Consolas, monospace; font-size: 9pt; background-color: #f3f4f6; padding: 6pt; margin: 6pt 0; white-space: pre-wrap; word-wrap: break-word; }
          blockquote { border-left: 2pt solid #d1d5db; padding-left: 8pt; margin: 6pt 0; color: #6b7280; font-size: 9pt; }
          .compact-list { margin: 0; padding-left: 14pt; }
          .compact-list li { margin: 0; }
        </style>
      </head>
      <body>
        ${title ? `<h2 style="color: #1e40af; border-bottom: 1px solid #93c5fd; padding-bottom: 3pt; margin-bottom: 8pt;">${title}</h2>` : ''}
        ${contentElement.innerHTML}
      </body>
      </html>
    `;

    // Create blob for HTML content
    const blob = new Blob([styledHtml], { type: 'text/html' });

    // Use ClipboardItem API for rich clipboard copying
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': blob,
        'text/plain': new Blob([contentElement.innerText], { type: 'text/plain' })
      })
    ]);

    return true;
  } catch (err) {
    // Fallback to plain text if ClipboardItem not supported
    try {
      await navigator.clipboard.writeText(contentElement?.innerText || '');
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Copy plain text to clipboard
 * @param {string} text - The text to copy
 * @returns {Promise<boolean>} - Whether the copy was successful
 */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
